// WebSocket connection management hook as specified in appmark.txt
import { useEffect, useState, useCallback } from "react";
import { webSocketService } from "../services/websocket";
import { WebSocketMessage } from "../types";

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  useEffect(() => {
    // Connect on mount
    webSocketService.connect();

    // Set up connection handler
    const removeConnectionHandler = webSocketService.addConnectionHandler(
      (connected) => {
        setIsConnected(connected);
      }
    );

    // Set up message handler
    const removeMessageHandler = webSocketService.addMessageHandler(
      (message) => {
        setLastMessage(message);
      }
    );

    // Initial connection status
    setIsConnected(webSocketService.getConnectionStatus());

    // Cleanup on unmount - remove handlers but keep connection alive (SWRM-111)
    // The WebSocket is a singleton, so don't disconnect when individual components unmount
    return () => {
      removeConnectionHandler();
      removeMessageHandler();
      // Don't disconnect - other components might still be using the WebSocket
    };
  }, []);

  const sendMessage = useCallback((message: any) => {
    webSocketService.send(message);
  }, []);

  return {
    isConnected,
    lastMessage,
    sendMessage,
  };
};

// Hook for subscribing to specific message types
export const useWebSocketMessages = <T extends WebSocketMessage["type"]>(
  messageType: T,
  handler: (data: any) => void
) => {
  useEffect(() => {
    const removeHandler = webSocketService.addMessageHandler((message) => {
      if (message.type === messageType) {
        handler(message.data);
      }
    });

    return removeHandler;
  }, [messageType, handler]);
};
